// src/pages/CompanyPages/CompanySettings.js

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Grid,
  Paper,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";

import axiosInstance from "../../Services/axiosConfig";
import Sidebar from "../../components/CompanySidebar";
import Footer from "../../components/Footer";
import FeedbackButton from "../../components/FeedbackButton";
import FeedbackSnackbar from "../../components/FeedbackSnackbar";
import { useThemeMode } from "../../components/ThemeModeContext";

export default function CompanySettings() {
  const { companyId } = useParams(); // Get companyId from URL
  const token = localStorage.getItem("authToken");

  // Estado de carregamento
  const [loading, setLoading] = useState(false);

  // Estados para feedback via Snackbar (se desejar usar)
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Formulário com dados da empresa
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    address: "",
    zip_code: "",
    city: "",
    phone: "",
    primary_color: "",
    secondary_color: "",
    tos: "",
    categories: [],
    logo: null,
    banner: null,
  });

  // Dark/Light Mode
  const { mode } = useThemeMode();
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          background: {
            default: mode === "light" ? "#FAF5E4" : "#121212",
            paper: mode === "light" ? "#FFFFFF" : "#1E1E1E",
          },
          text: {
            primary: mode === "light" ? "#1A202C" : "#FFFFFF",
          },
        },
      }),
    [mode]
  );

  // Fecha Snackbar
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  // Busca dados da empresa
  const fetchCompanyData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`company-info/${token}/${companyId}/`);
      if (response.data.status === "success") {
        const data = response.data.data;
        setFormData({
          name: data.name || "",
          description: data.description || "",
          address: data.address || "",
          zip_code: data.zip_code || "",
          city: data.city || "",
          phone: data.phone || "",
          primary_color: data.primary_color || "",
          secondary_color: data.secondary_color || "",
          tos: data.tos || "",
          categories: Array.isArray(data.categories)
            ? data.categories
            : JSON.parse(data.categories || "[]"),
          logo: data.logo || null,
          banner: data.banner || null,
        });
      }
    } catch (error) {
      console.error("Error fetching company data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Ao montar, busca dados
  useEffect(() => {
    fetchCompanyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Lida com mudanças no form
  const handleChange = (e) => {
    const { name, type, value, files } = e.target;
    if (type === "file") {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Lida com mudanças em 'categories'
  const handleCategoriesChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      categories: e.target.value.split(",").map((cat) => cat.trim()),
    }));
  };

  // Submete form
  const handleSubmit = async () => {
    setLoading(true);
    const data = new FormData();

    // Monta formData com JSON para 'categories'
    for (const key in formData) {
      if (formData[key] !== null) {
        if (key === "categories") {
          data.append(key, JSON.stringify(formData[key]));
        } else {
          data.append(key, formData[key]);
        }
      }
    }

    try {
      const response = await axiosInstance.post(`company-edit/${token}/${companyId}/`, data);
      if (response.data.status === "success") {
        Swal.fire("Success", "Company settings updated successfully", "success");
        fetchCompanyData();
      } else {
        Swal.fire("Error", response.data.message, "error");
      }
    } catch (error) {
      console.error("Error updating company:", error);
      Swal.fire("Error", "An error occurred. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      {/* Layout vertical */}
      <Box display="flex" flexDirection="column" minHeight="100vh" bgcolor="background.default">
        
        {/* Layout horizontal: Sidebar + conteúdo */}
        <Box display="flex" flexGrow={1}>
          <Sidebar companyId={companyId} />

          <Box flex={1} p={3} color="text.primary">
            <Typography variant="h4" gutterBottom>
              Company Settings
            </Typography>

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                {/* Primary Settings Block */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={3} sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Primary Settings
                    </Typography>

                    <TextField
                      label="Company Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      fullWidth
                      required
                      margin="normal"
                    />

                    <TextField
                      label="Description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      multiline
                      rows={4}
                      fullWidth
                      required
                      margin="normal"
                    />

                    <TextField
                      label="Address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      fullWidth
                      required
                      margin="normal"
                    />

                    <TextField
                      label="Zip Code"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      fullWidth
                      required
                      margin="normal"
                    />

                    <TextField
                      label="City"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      fullWidth
                      required
                      margin="normal"
                    />

                    <TextField
                      label="Phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      fullWidth
                      required
                      margin="normal"
                    />
                  </Paper>
                </Grid>

                {/* Secondary Settings Block */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={3} sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Secondary Settings
                    </Typography>

                    <Box display="flex" gap={2} sx={{ mb: 2 }}>
                      <TextField
                        label="Primary Color"
                        name="primary_color"
                        type="color"
                        value={formData.primary_color}
                        onChange={handleChange}
                        fullWidth
                        required
                      />

                      <TextField
                        label="Secondary Color"
                        name="secondary_color"
                        type="color"
                        value={formData.secondary_color}
                        onChange={handleChange}
                        fullWidth
                        required
                      />
                    </Box>

                    <TextField
                      label="Terms of Service (ToS)"
                      name="tos"
                      value={formData.tos}
                      onChange={handleChange}
                      multiline
                      rows={6}
                      fullWidth
                      required
                      margin="normal"
                    />

                    <TextField
                      label="Categories (comma-separated)"
                      name="categories"
                      value={Array.isArray(formData.categories) ? formData.categories.join(",") : ""}
                      onChange={handleCategoriesChange}
                      fullWidth
                      required
                      margin="normal"
                    />

                    <Button
                      variant="contained"
                      component="label"
                      fullWidth
                      sx={{ mt: 2 }}
                    >
                      Upload Logo
                      <input
                        type="file"
                        hidden
                        name="logo"
                        accept="image/*"
                        onChange={handleChange}
                      />
                    </Button>

                    <Button
                      variant="contained"
                      component="label"
                      fullWidth
                      sx={{ mt: 2 }}
                    >
                      Upload Banner
                      <input
                        type="file"
                        hidden
                        name="banner"
                        accept="image/*"
                        onChange={handleChange}
                      />
                    </Button>
                  </Paper>
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={loading}
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : "Save Settings"}
                  </Button>
                </Grid>
              </Grid>
            )}
          </Box>
        </Box>

        {/* FeedbackButton (DarkMode + Feedback) */}
        <FeedbackButton />

        {/* Footer */}
        <Footer />

        {/* Snackbar para feedback (se quiser usar) */}
        <FeedbackSnackbar
          open={openSnackbar}
          handleClose={handleCloseSnackbar}
          message={snackbarMsg}
          severity={snackbarSeverity}
        />
      </Box>
    </ThemeProvider>
  );
}
